import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { trackPageView } from '../lib/analytics';
import { Sparkles, Heart, Users, Scissors, Instagram, ArrowRight } from 'lucide-react';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

export default function AboutPage() {
  useEffect(() => { trackPageView('/nosotros', 'Nosotros'); }, []);

  return (
    <div className="min-h-screen bg-black pt-24" data-testid="about-page">
      <div className="max-w-3xl mx-auto px-4 md:px-8">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="pt-8 pb-10 text-center"
        >
          <h1 className="text-3xl md:text-4xl font-serif text-white mb-3">D'Avocado Swimwear</h1>
          <p className="text-white/30 text-sm">Diseño venezolano con alma caribeña</p>
        </motion.div>

        {/* Designer section */}
        <motion.div {...fadeIn} className="mb-16">
          <div className="bg-white/[0.02] rounded-2xl overflow-hidden">
            {/* Designer photo placeholder */}
            <div className="aspect-[16/9] md:aspect-[2/1] bg-gradient-to-br from-gold/5 via-[#111] to-avocado/5 flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=1200')] bg-cover bg-center opacity-30" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
              <div className="relative text-center px-6 pt-8">
                <span className="text-gold/60 text-xs uppercase tracking-[0.3em]">La diseñadora</span>
              </div>
            </div>

            <div className="p-6 md:p-8">
              <p className="text-white/50 text-sm leading-relaxed mb-4">
                Detrás de D'Avocado hay una diseñadora venezolana con una visión clara: crear trajes de baño
                que hagan sentir a cada mujer segura, elegante y única. Cada pieza nace de un proceso creativo
                que mezcla tendencias internacionales con la esencia vibrante del Caribe.
              </p>
              <p className="text-white/50 text-sm leading-relaxed">
                Con dedicación artesanal y atención obsesiva al detalle, cada diseño es pensado para realzar
                la belleza natural de quien lo lleva — sin importar su talla, su forma o su estilo.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Quote */}
        <motion.div {...fadeIn} className="mb-16 text-center px-4">
          <div className="relative">
            <span className="text-gold/20 text-6xl font-serif absolute -top-6 left-1/2 -translate-x-1/2">"</span>
            <p className="text-white/70 text-lg md:text-xl font-serif leading-relaxed italic pt-6">
              Cada mujer merece sentirse como la versión más hermosa de sí misma
            </p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <div className="w-8 h-[1px] bg-gold/30" />
              <span className="text-gold/50 text-xs tracking-wider uppercase">Fundadora</span>
              <div className="w-8 h-[1px] bg-gold/30" />
            </div>
          </div>
        </motion.div>

        {/* Values */}
        <motion.div {...fadeIn} className="mb-16">
          <h2 className="text-lg font-semibold text-white mb-5">Lo que nos define</h2>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Scissors, title: 'Diseño artesanal', desc: 'Cada pieza es cortada y cosida con dedicación manual' },
              { icon: Sparkles, title: 'Calidad premium', desc: 'Materiales seleccionados para durabilidad y confort' },
              { icon: Heart, title: 'Hecho con amor', desc: 'Desde Venezuela para mujeres que quieren brillar' },
              { icon: Users, title: 'Atención personal', desc: 'Te asesoramos uno a uno para encontrar tu pieza ideal' },
            ].map(({ icon: Icon, title, desc }, index) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="bg-white/[0.02] rounded-2xl p-5 group hover:bg-white/[0.04] transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-gold/5 flex items-center justify-center mb-3 group-hover:bg-gold/10 transition-colors">
                  <Icon className="w-4 h-4 text-gold/60" />
                </div>
                <h3 className="text-white text-sm font-medium mb-1">{title}</h3>
                <p className="text-white/25 text-xs leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Origin story */}
        <motion.div {...fadeIn} className="mb-16">
          <div className="bg-white/[0.02] rounded-2xl p-6 md:p-8">
            <span className="text-gold/40 text-xs uppercase tracking-widest">El nombre</span>
            <h2 className="text-xl font-serif text-white mt-2 mb-4">¿Por qué D'Avocado?</h2>
            <p className="text-white/40 text-sm leading-relaxed mb-3">
              El aguacate es uno de los frutos más emblemáticos del trópico — natural, fresco, lleno de vida.
              Así es como queremos que te sientas con nuestros diseños: auténtica y radiante.
            </p>
            <p className="text-white/40 text-sm leading-relaxed">
              D'Avocado nace de esa conexión con lo natural y lo caribeño. Es moda que no pretende ser algo
              que no es — es real, es fresca, es para vos.
            </p>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div {...fadeIn} className="mb-16">
          <div className="grid grid-cols-3 gap-3">
            {[
              { number: '5+', label: 'Años diseñando' },
              { number: '50+', label: 'Diseños únicos' },
              { number: '100%', label: 'Hecho a mano' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center py-6 bg-white/[0.02] rounded-2xl"
              >
                <p className="text-gold text-2xl md:text-3xl font-serif">{stat.number}</p>
                <p className="text-white/20 text-xs mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Instagram CTA */}
        <motion.div {...fadeIn} className="mb-12">
          <a
            href="https://instagram.com/davocadoswimwear"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between bg-white/[0.02] hover:bg-white/[0.04] rounded-2xl p-5 transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] flex items-center justify-center">
                <Instagram className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">@davocadoswimwear</p>
                <p className="text-white/25 text-xs">Seguinos en Instagram</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-white/40 transition-colors" />
          </a>
        </motion.div>

        {/* CTA */}
        <motion.div {...fadeIn} className="pb-16 text-center">
          <h2 className="text-xl font-serif text-white mb-3">¿Lista para brillar?</h2>
          <p className="text-white/30 text-sm mb-6">Explorá nuestra colección y encontrá tu pieza perfecta</p>
          <div className="flex flex-col sm:flex-row gap-2.5 justify-center">
            <Link
              to="/tienda"
              className="inline-flex items-center justify-center gap-2 h-11 px-6 bg-gold hover:bg-gold-hover text-black rounded-full text-sm font-medium transition-colors"
            >
              Ver colección
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="https://wa.me/584242925792?text=Hola!%20Quiero%20conocer%20más%20sobre%20D'Avocado"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-11 px-6 bg-white/[0.04] hover:bg-white/[0.08] text-white/60 rounded-full text-sm transition-colors"
            >
              Escribir por WhatsApp
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
